import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Column } from "primereact/column";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import LineApi from "../../services/LineApi";
import AreaApi from "../../services/AreaApi";
import ProductionUnitApi from "../../services/ProductionUnitApi";

// Design System
import DataTable from "../../components/DataTable/DataTable";
import FilterPanel from "../../components/FilterPanel/FilterPanel";
import Input, { type InputOption } from "../../components/Input/Input";
import { Button } from "../../components/Button/Button";
import { IconButton } from "../../components/IconButton/IconButton";
import PageHeader from "../../components/PageHeader/PageHeader";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import type { PaginatorPageChangeEvent } from "../../components/Paginator/Paginator";

const STORAGE_KEY = "TableFilterAdvanced_lines_filters";

interface Line {
  id: string;
  name: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
  areaId?: string;
  area?: { id: string; name: string } | null;
  areaName?: string;
  productionUnitId?: string;
  productionUnitName?: string;
}

const ListLines: React.FC = () => {
  const { plantId } = useAuth();
  const navigate = useNavigate();
  const [lines, setLines] = useState<Line[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { t: translateForm } = useTranslation("form");
  const { t: translateGeneral } = useTranslation("general");
  const { displayToast } = useToast();

  // Filter options
  const [areas, setAreas] = useState<InputOption[]>([]);
  const [productionUnits, setProductionUnits] = useState<InputOption[]>([]);

  // Filter state
  const [filterName, setFilterName] = useState<string>("");
  const [filterProductionUnit, setFilterProductionUnit] = useState<string[]>([]);
  const [filterArea, setFilterArea] = useState<string[]>([]);

  // Pagination / Sort
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [lineToDelete, setLineToDelete] = useState<string | null>(null);

  // Saved filters
  const [savedFilters, setSavedFilters] = useState<Record<string, any>>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  });

  // #region Data Loading

  useEffect(() => {
    if (!plantId) return;

    const loadFilterOptions = async (): Promise<void> => {
      try {
        const [areasResp, unitsResp] = await Promise.all([
          AreaApi.GetAreaByPlantIdStatic(plantId).catch((e: any) => {
            displayToast("error", translateForm("error"), e.message, false, 3000);
            return { data: [] as any[] };
          }),
          ProductionUnitApi.GetProductUnitByPlant(plantId).catch((e: any) => {
            displayToast("error", translateForm("error"), e.message, false, 3000);
            return { data: [] as any[] };
          }),
        ]);
        const areaItems = areasResp.data ?? [];
        const unitItems = unitsResp.data ?? [];
        setAreas(areaItems.map((a: any) => ({ id: a.id, label: a.name, value: a.id, description: a.name })));
        setProductionUnits(unitItems.map((u: any) => ({ id: u.id, label: u.name, value: u.id, description: u.name })));
      } catch (e) {
        console.error("Erro ao carregar opÃ§Ãµes de filtro:", e);
      }
    };

    loadFilterOptions();
    fetchLines(savedFilters, 0); // Busca inicial automÃ¡tica
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantId]);

  const fetchLines = async (
    filters: Record<string, any> = {},
    firstIndex = 0,
    field = sortField,
    direction: "asc" | "desc" = sortOrder,
    pageSize: number = rows,
  ): Promise<void> => {
    setIsLoading(true);
    try {
      const name: string | undefined = filters.name?.value ?? undefined;
      const productionUnitName: string | undefined = filters.productionUnitName?.value ?? undefined;
      const productionUnitIds: string[] = filters.productionUnitIds?.value ?? [];
      const areaIds: string[] = filters.areaName?.value || [];
      const version: number | undefined = filters.version?.value ?? undefined;

      const response = await LineApi.GetByFilterAsync(
        [plantId],
        Math.floor(firstIndex / pageSize) + 1,
        pageSize,
        field,
        direction,
        undefined, // code
        name,
        productionUnitName,
        areaIds,
        productionUnitIds,
        version,
      );

      const payload = response?.data ?? {};
      const data = Array.isArray(payload.Lines) ? payload.Lines : Array.isArray(payload.lines) ? payload.lines : Array.isArray(payload.items) ? payload.items : Array.isArray(response?.data) ? response.data : [];
      const total = payload.Total ?? payload.TotalRecords ?? payload.total ?? response?.data?.total ?? data.length;

      setTotalRecords(total ?? data.length);

      setLines(
        data.map((ln: any): Line => ({
          id: ln.id,
          name: ln.name ?? "",
          createdAt: ln.createdAt,
          updatedAt: ln.updatedAt,
          createdBy: ln.createdBy,
          updatedBy: ln.updatedBy,
          areaId: ln.areaId,
          area: ln.area
            ? { id: ln.area.id, name: ln.area.name }
            : ln.areaName
              ? { id: ln.areaId, name: ln.areaName }
              : null,
          areaName: ln.area?.name ?? ln.areaName ?? "",
          productionUnitName: ln.area?.productionUnit?.name ?? ln.productionUnitName ?? "",
        })),
      );
    } catch (error) {
      setLines([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  };

  // #endregion

  // #region Handlers

  const handleFilter = (): void => {
    const filters: Record<string, any> = {
      name: filterName ? { value: filterName } : undefined,
      productionUnitIds: filterProductionUnit.length > 0 ? { value: filterProductionUnit } : undefined,
      areaName: filterArea.length > 0 ? { value: filterArea } : undefined,
    };

    Object.keys(filters).forEach((key) => {
      if (filters[key] === undefined) delete filters[key];
    });

    setPage(0);
    setSavedFilters(filters);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    fetchLines(filters, 0);
  };

  const handleClearFilters = (): void => {
    setFilterName("");
    setFilterProductionUnit([]);
    setFilterArea([]);
    setPage(0);
    setSavedFilters({});
    localStorage.removeItem(STORAGE_KEY);
    fetchLines({}, 0);
  };

  const handlePage = (e: PaginatorPageChangeEvent): void => {
    setPage(e.first);
    setRows(e.rows);
    fetchLines(savedFilters, e.first, sortField, sortOrder, e.rows);
  };

  const onSort = (e: any): void => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder === 1 ? "asc" : "desc");
    fetchLines(savedFilters, page, e.sortField, e.sortOrder === 1 ? "asc" : "desc");
  };

  // #endregion

  // #region Actions

  async function deleteLine(id: string): Promise<void> {
    try {
      await LineApi.Delete(id);
      setLines((prev) => prev.filter((l) => l.id !== id));
      displayToast("success", translateForm("success"), translateForm("lineSuccessfullyDeleted"), false, 3000);
    } catch (error: any) {
      const apiErrors = error?.response?.data;
      const messages: string = Array.isArray(apiErrors)
        ? apiErrors.map((err: any) => `â€¢ ${err.data?.data || err.data || err}`).join("\n")
        : error?.response?.data?.data || error?.message || "Unknown error";
      displayToast("error", translateForm("lineNotDeleted"), messages, false, 5000);
    }
  }

  const confirmDelete = (id: string): void => {
    setLineToDelete(id);
    setShowConfirmDelete(true);
  };

  // #endregion

  // #region Action Column Template

  const actionBodyTemplate = (rowData: Line): JSX.Element => {
    return (
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <IconButton
          icon="visibility"
          variant="text"
          severity="secondary"
          onClick={() => navigate(`/lines/${rowData.id}`)}
          aria-label={translateGeneral("seeDetails")}
        />
        <IconButton
          icon="edit"
          variant="text"
          severity="secondary"
          onClick={() => navigate(`/lines/${rowData.id}/edit`)}
          aria-label={translateGeneral("edit")}
        />
        <IconButton
          icon="account_tree"
          variant="text"
          severity="secondary"
          onClick={() => navigate(`/lines/assign-families/${rowData.id}`)}
          aria-label={translateForm("linkFamilies")}
        />
        <IconButton
          icon="delete"
          variant="text"
          severity="secondary"
          onClick={() => confirmDelete(rowData.id)}
          aria-label={translateGeneral("delete")}
        />
      </div>
    );
  };

  // #endregion

  return (
    <div className="overflow-hidden">
      <PageHeader
        title={translateForm("lines")}
        description={translateForm("lineDescription") || "Gerencie as linhas de produÃ§Ã£o do sistema e suas configuraÃ§Ãµes"}
      >
        <Button
          label={translateForm("register")}
          icon="add"
          severity="primary"
          onClick={() => navigate("/lines/create")}
        />
      </PageHeader>

      <div>
        <ConfirmModal
          visible={showConfirmDelete}
          onHide={() => {
            setShowConfirmDelete(false);
            setLineToDelete(null);
          }}
          onConfirm={() => lineToDelete && deleteLine(lineToDelete)}
          title={translateGeneral("confirmation")}
          message={translateForm("lineDeleteConfirmMessage")}
          severity="danger"
          confirmLabel={translateForm("yes")}
          cancelLabel={translateForm("no")}
        />
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {/* FilterPanel */}
          <FilterPanel
            title={translateGeneral("filters") || "Filtros"}
            showFilterIcon
            onApply={handleFilter}
            onClear={handleClearFilters}
          >
            <Input
              type="text"
              label={translateForm("name")}
              placeholder={translateForm("searchByName") || "Pesquisar por nome"}
              value={filterName}
              onChange={(value) => setFilterName(value)}
            />
            <Input
              type="multiselect"
              label={translateForm("productionUnit")}
              placeholder={translateForm("selectProductionUnits") || "Selecione as unidades de produÃ§Ã£o"}
              value={filterProductionUnit}
              onChange={(value) => setFilterProductionUnit(value)}
              options={productionUnits}
            />
            <Input
              type="multiselect"
              label={translateForm("area")}
              placeholder={translateForm("selectAreas") || "Selecione as Ã¡reas"}
              value={filterArea}
              onChange={(value) => setFilterArea(value)}
              options={areas}
            />
          </FilterPanel>

          {/* DataTable */}
          <DataTable
            value={lines}
            paginated
            first={page}
            rows={rows}
            totalRecords={totalRecords}
            onPageChange={handlePage}
            rowsPerPageOptions={[10, 20, 50]}
            loading={isLoading}
            emptyMessage={translateForm("noRecordsFound") || "Nenhum registro encontrado"}
            sortField={sortField}
            sortOrder={sortOrder === "asc" ? 1 : -1}
            onSort={onSort}
            resizableColumns
          >
            <Column
              field="productionUnitName"
              header={translateForm("productionUnit")}
              sortable
              style={{ minWidth: "10rem" }}
              body={(rowData: Line) => rowData.productionUnitName || "N/A"}
            />
            <Column
              field="areaName"
              header={translateForm("area")}
              sortable
              style={{ minWidth: "10rem" }}
              body={(rowData: Line) => rowData.areaName || "N/A"}
            />
            <Column
              field="name"
              header={translateForm("name")}
              sortable
            />
            <Column
              field="actions"
              header={translateForm("actions")}
              body={actionBodyTemplate}
              style={{ width: "9rem" }}
              resizeable={false}
            />
          </DataTable>
        </div>
      </div>
    </div>
  );
};

export default ListLines;
